export type AgeFilter = "all" | "young" | "prime" | "veteran";
export type PriceFilter = "all" | "minimum" | "affordable";
export type SortKey = "ovr" | "pot" | "asking";

export type FreeAgentFilterState = {
	ageFilter: AgeFilter;
	positionFilter: string;
	priceFilter: PriceFilter;
	sortKey: SortKey;
	upsideOnly: boolean;
};

export type FreeAgentTaskingPlayer = {
	age: number;
	contract: {
		amount: number;
	};
	freeAgentType: string;
	mood: {
		user: {
			willing: boolean;
		};
	};
	pid: number;
	ratings: {
		ovr: number;
		pos: string;
		pot: number;
	};
};

export const freeAgentRecommendationLimit = 3;

export const getEffectiveFreeAgentSortKey = ({
	challengeNoRatings,
	sortKey,
}: {
	challengeNoRatings: boolean;
	sortKey: SortKey;
}) => (challengeNoRatings && sortKey !== "asking" ? "asking" : sortKey);

export const getActiveFreeAgentFilterCount = ({
	ageFilter,
	positionFilter,
	priceFilter,
	upsideOnly,
}: FreeAgentFilterState) =>
	Number(positionFilter !== "all") +
	Number(ageFilter !== "all") +
	Number(priceFilter !== "all") +
	Number(upsideOnly);

const hasHighUpside = (p: FreeAgentTaskingPlayer) => p.ratings.pot - p.ratings.ovr >= 10;

export const getFilteredFreeAgents = <P extends FreeAgentTaskingPlayer>({
	canAfford,
	challengeNoRatings,
	filters,
	minContractAmount,
	players,
}: {
	canAfford: (p: P) => boolean;
	challengeNoRatings: boolean;
	filters: FreeAgentFilterState;
	minContractAmount: number;
	players: readonly P[];
}) => {
	const effectiveSortKey = getEffectiveFreeAgentSortKey({
		challengeNoRatings,
		sortKey: filters.sortKey,
	});

	return players
		.filter((p) => {
			if (
				filters.positionFilter !== "all" &&
				p.ratings.pos !== filters.positionFilter
			) {
				return false;
			}
			if (filters.ageFilter === "young" && p.age > 24) {
				return false;
			}
			if (filters.ageFilter === "prime" && (p.age < 25 || p.age > 30)) {
				return false;
			}
			if (filters.ageFilter === "veteran" && p.age < 31) {
				return false;
			}
			if (
				!challengeNoRatings &&
				filters.upsideOnly &&
				!hasHighUpside(p)
			) {
				return false;
			}
			if (
				filters.priceFilter === "minimum" &&
				p.contract.amount > minContractAmount
			) {
				return false;
			}
			if (filters.priceFilter === "affordable" && !canAfford(p)) {
				return false;
			}
			return true;
		})
		.sort((a, b) => {
			if (effectiveSortKey === "pot") {
				return b.ratings.pot - a.ratings.pot;
			}
			if (effectiveSortKey === "asking") {
				return a.contract.amount - b.contract.amount;
			}
			return b.ratings.ovr - a.ratings.ovr;
		});
};

const getRecommendationScore = ({
	challengeNoRatings,
	p,
	rosterNeedPositions,
}: {
	challengeNoRatings: boolean;
	p: FreeAgentTaskingPlayer;
	rosterNeedPositions: ReadonlySet<string>;
}) =>
	Number(rosterNeedPositions.has(p.ratings.pos)) * 500 +
	Number(!challengeNoRatings && hasHighUpside(p)) * 250 +
	Number(p.mood.user.willing) * 100 +
	(challengeNoRatings ? 0 : p.ratings.ovr * 2 + p.ratings.pot) -
	p.contract.amount / 50;

export const getFreeAgentRecommendationBuckets = <
	P extends FreeAgentTaskingPlayer,
>({
	canAfford,
	challengeNoRatings,
	limit = freeAgentRecommendationLimit,
	players,
	rosterNeedPositions,
}: {
	canAfford: (p: P) => boolean;
	challengeNoRatings: boolean;
	limit?: number;
	players: readonly P[];
	rosterNeedPositions: ReadonlySet<string>;
}) => {
	const originalIndex = new Map(players.map((p, index) => [p.pid, index]));
	const recommended = players
		.filter(
			(p) =>
				p.freeAgentType === "available" &&
				p.mood.user.willing &&
				canAfford(p),
		)
		.sort((a, b) => {
			const scoreDiff =
				getRecommendationScore({
					challengeNoRatings,
					p: b,
					rosterNeedPositions,
				}) -
				getRecommendationScore({
					challengeNoRatings,
					p: a,
					rosterNeedPositions,
				});
			if (scoreDiff !== 0) {
				return scoreDiff;
			}
			return (originalIndex.get(a.pid) ?? 0) - (originalIndex.get(b.pid) ?? 0);
		})
		.slice(0, limit);

	const recommendedPids = new Set(recommended.map((p) => p.pid));

	return {
		recommended,
		remaining: players.filter((p) => !recommendedPids.has(p.pid)),
	};
};
